import { Alert, Anchor, Badge, Tabs, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { useIslandToday, useLocationId } from '../hooks/useSeedLocation';
import { useTabInUrl } from '../hooks/useTabInUrl';
import { enumOptions } from '../lib/enums';
import { payPhrase } from './staff/pay';
import {
  onlyStaffSurfaces,
  PERMISSIONS_PATH,
  type Permission,
  type RoleGrant,
  rolePermissionsPath,
} from './staff/permissions';

interface CurrentPay {
  staffId: string;
  amountMinor: number;
  currency: string;
  type: string;
  effectiveFrom: string;
}

const COMPENSATIONS_PATH = '/admin/v1/payroll/compensations';

/**
 * What one role actually lets somebody do, on the list of roles.
 *
 * A role's name and the owner's own note about it say nothing about what a register will allow — the
 * grants behind it are the whole content of a role, and until this column existed the only way to
 * find out was to open all eight of them one at a time. The failure that matters is quieter than
 * inconvenience: a role with nothing granted looks exactly like a role that covers the job, so a
 * new bartender gets past the PIN pad and can then do nothing, and nobody finds out until the
 * middle of a Saturday service.
 *
 * One request per role, which is small and cached; the catalog request is shared with the role
 * page, which is usually where the reader is going next anyway.
 */
function RoleReach({ roleId }: { roleId: string }) {
  const grants = useQuery({
    queryKey: listKey(rolePermissionsPath(roleId)),
    queryFn: () => rawApi<RoleGrant[]>('GET', rolePermissionsPath(roleId)),
  });
  const catalog = useQuery({
    queryKey: listKey(PERMISSIONS_PATH),
    queryFn: () => rawApi<Permission[]>('GET', PERMISSIONS_PATH),
  });

  // A dash while the answer is still coming, and a dash if it never came. Printing "allows
  // nothing" over a request that failed would send the owner ticking boxes back on a role that
  // was already right.
  if (!grants.data || !catalog.data) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  const granted = new Set(grants.data.map((g) => g.permissionId));
  const held = onlyStaffSurfaces(catalog.data).filter((p) => granted.has(p.id));
  const canDo = held.filter((p) => p.mode === 'perform').length;
  const canApprove = held.filter((p) => p.mode === 'approve').length;

  if (canDo === 0 && canApprove === 0) {
    return (
      <Badge color="yellow" variant="light">
        Allows nothing yet
      </Badge>
    );
  }

  // The same two words the role's own page uses for the same two counts, so a manager moving
  // between the list and the page is not asked to match up two vocabularies.
  const parts: string[] = [];
  if (canDo > 0) parts.push(`can do ${canDo} ${canDo === 1 ? 'thing' : 'things'}`);
  if (canApprove > 0) parts.push(`can approve ${canApprove}`);
  const summary = parts.join(' · ');
  return <Text size="sm">{summary.charAt(0).toUpperCase() + summary.slice(1)}</Text>;
}

export function StaffPage() {
  const locationId = useLocationId();

  /*
   * Which tab you are on is in the address, so a role page can send you back to the roles you came
   * from. Its back link says "All roles" and used to land on the list of people, because the tab
   * lived in component state that a fresh page load knows nothing about.
   */
  const [tab, setTab] = useTabInUrl(['members', 'roles'] as const, 'members');

  /**
   * What each person is paid today, shown on the list rather than one click away.
   *
   * Somebody with no rate on file is silently left off the pay register — the omission only
   * surfaces after payroll has been built, on a page nobody opens until payday. This is the screen
   * where a manager looks at the whole team, so it is where "nobody has set Yolany's pay" belongs.
   *
   * The endpoint answers with every dated record for everyone, newest first, so the first row for a
   * person that has already taken effect is the rate in force. A raise dated next month is real but
   * is not what they are paid now.
   */
  const pay = useQuery({
    queryKey: listKey(COMPENSATIONS_PATH),
    queryFn: () => rawApi<CurrentPay[]>('GET', COMPENSATIONS_PATH),
  });

  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const today = useIslandToday();
  const currentPay = new Map<string, CurrentPay>();
  for (const row of pay.data ?? []) {
    if (row.effectiveFrom.slice(0, 10) > today) continue;
    if (!currentPay.has(row.staffId)) currentPay.set(row.staffId, row);
  }

  return (
    <>
      <Title order={3} mb="md">
        Staff
      </Title>
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="members">People</Tabs.Tab>
          <Tabs.Tab value="roles">Roles</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="members">
          {/*
            A list, and only a list: find a person, see the two or three facts worth scanning, open
            them. The Edit button that used to sit on every row opened a dialog editing a handful of
            the same fields the person's own page owns — so a phone number had two homes, and which
            one was right depended on which one the manager happened to use. The form below is for
            adding somebody who is not here yet; everything after that happens on their page.
          */}
          <CrudSection
            title="People"
            entityNoun="staff member"
            description="Everyone who works here. Open someone to change their details, pay, roles and schedule."
            listPath="/admin/v1/staff/members"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.displayName ?? 'this person')}
            emptyMessage="No one added yet. Add your first staff member to get started."
            columns={[
              {
                accessor: 'displayName',
                title: 'Name',
                render: (r) => (
                  <Anchor component={Link} to={`/staff/${String(r.id)}`} fw={600}>
                    {String(r.displayName ?? '—')}
                  </Anchor>
                ),
              },
              {
                accessor: 'id',
                title: 'Paid',
                noWrap: true,
                render: (r) => {
                  const rate = currentPay.get(String(r.id));
                  if (rate) return payPhrase(rate.amountMinor, rate.currency, rate.type);
                  // `!pay.data` covers the failed request as well as the pending one. If the rates
                  // could not be fetched, the honest answer is a dash on every row — telling the
                  // owner that nobody on his team has a pay rate, because a query 404'd, is worse
                  // than telling him nothing. Beyond that: only worth shouting about for someone
                  // who still works here; a leaver with no rate on file is history, not a job.
                  if (!pay.data || r.status !== 'active') return '—';
                  return (
                    <Badge color="yellow" variant="light">
                      No pay rate
                    </Badge>
                  );
                },
              },
              { accessor: 'status', title: 'Status', enumGroup: 'staffStatus' },
              {
                accessor: 'contactPhone',
                title: 'Phone',
                noWrap: true,
                // Callable, not just readable. The reason a manager looks a member of staff up on a
                // Saturday is to ask them to cover a shift.
                render: (r) =>
                  r.contactPhone ? (
                    <Anchor href={`tel:${String(r.contactPhone).replace(/[^\d+]/g, '')}`}>
                      {String(r.contactPhone)}
                    </Anchor>
                  ) : (
                    '—'
                  ),
              },
            ]}
            /*
             * Only what is known the day somebody is hired. Employment status and a last day worked
             * are not facts about a new starter — they are what changes later, on their own page,
             * where the two are validated against each other and where a leaver's final pay is
             * worked out from the date.
             */
            fields={[
              {
                name: 'displayName',
                label: 'Name',
                type: 'text',
                required: true,
                description: 'How they appear on the register and on rotas',
              },
              { name: 'fullName', label: 'Full legal name', type: 'text', nullable: true },
              {
                name: 'language',
                label: 'Language',
                type: 'select',
                options: enumOptions('language'),
                /*
                  NOT "and printed tickets". A factura prints the words the tax office reads and
                  nothing else decides them (domains/fiscal/pdf.ts), so the printed half of this
                  sentence could never come true however much of the app was built — and a manager
                  who read it would set a bartender to Spanish and expect the paper to follow.

                  It says WHEN, for the reason PersonDetailsPanel.tsx sets out at the same field:
                  the register switches at sign-in and switches back at sign-out, and the flag in
                  the corner of the register is the terminal's own language underneath it.
                */
                description: 'The language the register switches to when they sign in',
              },
              { name: 'hiredAt', label: 'Start date', type: 'date', nullable: true },
              { name: 'contactPhone', label: 'Phone', type: 'text', nullable: true },
              { name: 'contactEmail', label: 'Email', type: 'text', nullable: true },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="roles">
          {/*
            The previous wording said a role "does not by itself decide what they can open or
            change". Half of that was true when nothing had ever been granted, and it is now simply
            wrong: a role decides what the register allows. What has to stay said — and said in a way
            that cannot be read the other way round — is WHERE it applies. Staff have no accounts on
            this site and never will, so a sentence that leaves the door open to "role = access
            here" would have the owner locking down a door that does not exist while the one that
            does stands open.
          */}
          <Alert color="blue" variant="light" title="Where a role applies" mb="md">
            A role decides what someone can do at the register, the kitchen screen and the stock count once
            they have signed in with their PIN. It has nothing to do with this site — staff do not have
            accounts here. Open a role to say what it covers and who does it.
          </Alert>
          <CrudSection
            title="Roles"
            entityNoun="role"
            description="The jobs people do here. Someone can hold more than one, and gets everything both allow."
            listPath="/admin/v1/staff/roles"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.name ?? 'this role')}
            emptyMessage="No roles yet. Add the jobs people do here — Bartender, Kitchen, Shift Lead."
            columns={[
              {
                accessor: 'name',
                title: 'Role',
                // `seeded` marks the roles that came with the system, and a manager needs to know
                // which those are before trying to rename one. It rides with the name rather than
                // taking a column of its own to answer "yes" in most rows and nothing in the rest.
                render: (r) => (
                  <>
                    <Anchor component={Link} to={`/staff/roles/${String(r.id)}`} fw={600}>
                      {String(r.name ?? '—')}
                    </Anchor>
                    {r.seeded ? (
                      <Badge ml="xs" size="sm" variant="light" color="gray">
                        Built in
                      </Badge>
                    ) : null}
                  </>
                ),
              },
              { accessor: 'description', title: 'Description' },
              {
                accessor: 'id',
                title: 'What it allows',
                noWrap: true,
                render: (r) => <RoleReach roleId={String(r.id)} />,
              },
            ]}
            fields={[
              {
                name: 'name',
                label: 'Name',
                type: 'text',
                required: true,
                description: 'What the job is called — Bartender, Kitchen, Shift Lead',
              },
              {
                name: 'description',
                label: 'Description',
                type: 'text',
                nullable: true,
                description: 'What the job covers, in your own words',
              },
            ]}
          />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
